import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Skeleton,
  Pagination,
  Button,
  IconButton,
  Snackbar,
} from '@mui/material';
import {
  Send as PublishIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  VisibilityOutlined as PreviewIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PostDetailDialog, DetailPost } from '../components/PostDetailDialog';
import { PostPreviewDialog } from '../components/PostPreviewDialog';
import { PlatformOutcomes } from '../components/PlatformOutcomes';
import { EditPostDialog } from '../components/EditPostDialog';
import { SchedulePostDialog } from '../components/SchedulePostDialog';
import { DeletePostDialog } from '../components/DeletePostDialog';
import { PostEngagement } from '../components/PostEngagement';
import { usePostActions } from '../components/usePostActions';
import { DeletionHistory } from '../components/DeletionHistory';
import { getPlatformColor, getPlatformName } from '../config/platforms';
import { statusMeta, FILTERABLE_STATUSES } from '../config/postStatus';
import { PostThumbnail } from '../components/PostThumbnail';

const API_URL = process.env.REACT_APP_API_URL || '';

interface PostRow extends DetailPost {
  created_at: string;
}

/** Networks on this post that publish at different moments. */
const isStaggered = (post: PostRow): boolean =>
  new Set((post.platforms || []).map(p => post.schedule_variants?.[p] || post.scheduled_for)).size > 1;

/**
 * Each network's own publish time, earliest first.
 *
 * A platform without its own entry falls back to the base time, matching what the scheduler
 * actually does rather than leaving it blank.
 */
const perPlatformTimes = (post: PostRow): { platform: string; when: string }[] =>
  (post.platforms || [])
    .map(platform => ({
      platform,
      iso: post.schedule_variants?.[platform] || post.scheduled_for || '',
    }))
    .filter(x => x.iso)
    .sort((a, b) => a.iso.localeCompare(b.iso))
    .map(({ platform, iso }) => ({
      platform,
      when: new Date(iso).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      }),
    }));

const formatWhen = (post: PostRow) => {
  // A post with per-network times has no single date, and its base time may already have
  // passed for the networks that have published — so a scheduled post is dated by the next
  // window still to come. With one shared time the two are the same value.
  const stamp = post.published_at
    || (post.status === 'scheduled' ? post.next_due_at : null)
    // A staged draft keeps its old times, but is not due at them — date it by when it was made
    || (post.status !== 'draft' ? post.scheduled_for : null)
    || post.created_at;
  if (!stamp) return '';
  // SQLite CURRENT_TIMESTAMP is UTC without a zone marker; treat it as such
  const iso = stamp.includes('T') ? stamp : stamp.replace(' ', 'T') + 'Z';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return stamp;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const PostHistory: React.FC = () => {
  const { token, user } = useAuth();
  // The filter lives in the URL so the Dashboard tiles can link straight to a slice of
  // history ("Published: 42" → the published posts) and the view is shareable.
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get('status') || 'all';
  /**
   * Search text, also in the URL so a search is linkable and survives a refresh.
   *
   * `searchBox` is what is being typed; `search` is what has been committed. Writing every
   * keystroke to the URL would push a history entry per character and re-query on each one.
   */
  const search = searchParams.get('q') || '';
  const [searchBox, setSearchBox] = useState(search);

  const [posts, setPosts] = useState<PostRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  // Counts come from a separate unpaginated call, so the filter chips stay accurate
  const [counts, setCounts] = useState<Record<string, number>>({});

  const PAGE_SIZE = 12;

  // Clicking a card opens the full post: the cards clamp to four lines, so there was no
  // way to read a long draft or check how it would look before publishing.
  const [selected, setSelected] = useState<PostRow | null>(null);
  // A post the URL asked for that is not in the current list — a deleted one, or one on
  // another page. Kept separate from `posts` so paging and filtering do not drop it.
  const [linkedPost, setLinkedPost] = useState<PostRow | null>(null);
  const [linkedError, setLinkedError] = useState('');
  const [previewing, setPreviewing] = useState<PostRow | null>(null);
  const [editing, setEditing] = useState<PostRow | null>(null);
  const [scheduling, setScheduling] = useState<PostRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<PostRow | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [targets, setTargets] = useState<Record<string, string | null>>({});

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      // Filtering is done server-side now: with pagination, filtering the loaded page
      // would only ever search within that page.
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(page * PAGE_SIZE),
      });
      if (filter !== 'all') params.set('status', filter);
      if (search) params.set('q', search);

      const response = await fetch(`${API_URL}/api/posts?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        setPosts(await response.json());
        setTotal(Number(response.headers.get('X-Total-Count') || 0));
      } else {
        setError('Failed to load posts');
      }
    } catch (err) {
      setError('Network error loading posts');
    } finally {
      setLoading(false);
    }
  }, [token, page, filter, search]);

  // Totals per status, independent of the current page
  const loadCounts = useCallback(async () => {
    try {
      // One request for every status (it used to be one per status, seven in all)
      const res = await fetch(`${API_URL}/api/posts/counts`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const body: { counts: Record<string, number> } = await res.json();
      const byStatus = Object.fromEntries(FILTERABLE_STATUSES.map(st => [st, body.counts[st] || 0]));
      setCounts({
        ...byStatus,
        // Sum of the filterable statuses only, as before, so "All" matches the buttons beside it
        all: Object.values(byStatus).reduce((sum: number, n) => sum + (n as number), 0),
      });
    } catch (err) {
      // Counts are decoration; a failure should not blank the page
    }
  }, [token]);

  // Refresh both the page and the counts after any action: publishing moves a post between
  // statuses, so a stale count would contradict the list next to it.
  const refresh = useCallback(() => {
    loadPosts();
    loadCounts();
  }, [loadPosts, loadCounts]);

  const actions = usePostActions(refresh);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  useEffect(() => {
    fetch(`${API_URL}/api/platforms`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (!data) return;
        setTargets(
          Object.fromEntries(
            data.platforms.map((p: { platform: string; target: string | null }) => [p.platform, p.target])
          )
        );
      })
      .catch(() => {
        /* preview falls back to the platform name */
      });
  }, [token]);

  // Keep the open dialog in step with refreshed data: after publishing, the dialog should
  // show the new status and per-platform results rather than the pre-publish snapshot.
  //
  // A post opened from a `?post=` link is deliberately exempt: a deleted one is not in
  // `posts` at all, so matching against the list would find nothing and the guard below
  // would leave a stale card open rather than closing it.
  useEffect(() => {
    if (!selected || linkedPost?.id === selected.id) return;
    const fresh = posts.find(p => p.id === selected.id);
    if (fresh && fresh !== selected) setSelected(fresh);
  }, [posts, selected, linkedPost]);

  /**
   * Open one post directly, from `?post=<id>`.
   *
   * The Activity Log links here, and a log entry can reference a post that has since been
   * deleted or that sits on another page of history — so the post is fetched by id rather
   * than looked up in the current list. `includeDeleted` is what makes a deleted post
   * openable at all; the endpoint restricts it to admins.
   */
  useEffect(() => {
    const wanted = searchParams.get('post');
    if (!wanted) {
      setLinkedPost(null);
      return;
    }
    if (linkedPost?.id === wanted) return;

    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/posts/${wanted}?includeDeleted=true`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (!r.ok) {
          setLinkedError(
            r.status === 404
              ? 'That post no longer exists — it may have been permanently removed after 30 days.'
              : 'Could not open that post.'
          );
          return;
        }
        const post = await r.json();
        if (cancelled) return;
        setLinkedPost(post);
        setSelected(post);
      } catch {
        if (!cancelled) setLinkedError('Could not open that post.');
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, token]);

  // Both of these edit the existing params rather than replacing them, so changing the filter
  // does not silently discard an active search and vice versa.
  const setFilter = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('status');
    else next.set('status', value);
    setSearchParams(next, { replace: true });
    setPage(0);
  };

  const commitSearch = (value: string) => {
    const next = new URLSearchParams(searchParams);
    const trimmed = value.trim();
    if (trimmed) next.set('q', trimmed);
    else next.delete('q');
    setSearchParams(next, { replace: true });
    setPage(0);
  };

  const pageCount = Math.ceil(total / PAGE_SIZE);

  if (loading) {
    // Card-shaped skeletons in the real grid rather than a spinner on a blank page: the
    // layout is already known, so showing it settled means nothing jumps when the posts
    // arrive, and the wait reads as loading rather than as an empty page.
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">Loading posts…</Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              {/* 140px matches the real thumbnail height exactly, so the grid does not
                  reflow when the images replace these. */}
              <Skeleton variant="rectangular" height={140} />
              <CardContent>
                <Skeleton width="40%" height={24} sx={{ mb: 1 }} />
                <Skeleton />
                <Skeleton width="70%" />
                <Skeleton width="30%" height={20} sx={{ mt: 1.5 }} />
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
        Post History
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Every post and what actually happened on each platform.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {actions.error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={actions.clearMessages}>
          {actions.error}
        </Alert>
      )}

      {/* A `?post=` link that could not be opened — usually a post purged after 30 days.
          Worth stating: arriving at an ordinary list with no explanation reads as a bug. */}
      {linkedError && (
        <Alert severity="info" sx={{ mb: 3 }} onClose={() => setLinkedError('')}>
          {linkedError}
        </Alert>
      )}

      {user?.role === 'admin' && (
        <Box sx={{ mb: 2 }}>
          <Button size="small" onClick={() => setShowDeleted(v => !v)}>
            {showDeleted ? 'Hide deleted posts' : 'Deleted posts'}
          </Button>
        </Box>
      )}

      {showDeleted && (
        <DeletionHistory
          onRestored={refresh}
          // Routes through the URL rather than setting state directly, so opening a deleted
          // post from here behaves the same as arriving from the Activity Log — one code path,
          // and the view stays shareable.
          onOpenPost={id => setSearchParams({ post: id }, { replace: false })}
        />
      )}

      {/* Search commits on Enter or blur rather than per keystroke: every keystroke would be
          a URL write and a round trip, and this is a LIKE over the whole table. */}
      <TextField
        size="small"
        fullWidth
        placeholder="Search posts…"
        value={searchBox}
        onChange={e => setSearchBox(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') commitSearch(searchBox); }}
        onBlur={() => { if (searchBox.trim() !== search) commitSearch(searchBox); }}
        sx={{ mb: 2, maxWidth: 420 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="disabled" />
            </InputAdornment>
          ),
          endAdornment: search ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label="Clear search"
                onClick={() => { setSearchBox(''); commitSearch(''); }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      {search && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {total === 0
            ? `Nothing matches "${search}".`
            : `${total} post${total === 1 ? '' : 's'} matching "${search}"${filter !== 'all' ? ` in ${filter}` : ''}.`}
        </Typography>
      )}

      <ToggleButtonGroup
        value={filter}
        exclusive
        size="small"
        onChange={(_, value) => { if (value) setFilter(value); }}
        sx={{ mb: 3, flexWrap: 'wrap' }}
      >
        <ToggleButton value="all">All ({counts.all || 0})</ToggleButton>
        {FILTERABLE_STATUSES.map(status => (
          <ToggleButton key={status} value={status}>
            {statusMeta(status).label} ({counts[status] || 0})
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {posts.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              {filter === 'all'
                ? 'No posts yet. Create one from the Create Post page and it will appear here.'
                : `No ${statusMeta(filter).label.toLowerCase()} posts.`}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {posts.map(post => {
            const meta = statusMeta(post.status);
            const thumbnail = post.media_urls?.[0];
            const busy = actions.busyId === post.id;
            // The first reason a platform did not go out, as a one-line summary on the card
            const firstProblem = post.results?.find(r => r.status !== 'success');

            return (
              <Card
                key={post.id}
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderLeft: 4,
                  borderLeftColor: meta.color === 'default' ? 'divider' : `${meta.color}.main`,
                  transition: 'box-shadow 120ms',
                  '&:hover': { boxShadow: 3 },
                }}
              >
                {/* Only the body opens the detail view — the action row must not, or every
                    button press would also open a dialog behind it. */}
                <Box
                  onClick={() => setSelected(post)}
                  sx={{ cursor: 'pointer', flexGrow: 1, display: 'flex', flexDirection: 'column' }}
                >
                  {thumbnail && <PostThumbnail src={thumbnail} />}
                  <CardContent
                    sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Chip
                        icon={meta.icon}
                        label={meta.label}
                        color={meta.color}
                        size="small"
                        variant={meta.color === 'default' ? 'outlined' : 'filled'}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {post.status === 'scheduled' ? 'Due ' : ''}
                        {formatWhen(post)}
                      </Typography>
                    </Box>

                    {/* A post split across networks has no single time, and the line above
                        shows only the next one — so the card claimed one time for a post
                        going out at four. Each network gets its own line. */}
                    {post.status === 'scheduled' && isStaggered(post) && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        {perPlatformTimes(post).map(({ platform, when }) => (
                          <Box key={platform} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box
                              sx={{
                                width: 7, height: 7, borderRadius: '50%',
                                bgcolor: getPlatformColor(platform), flexShrink: 0,
                              }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {getPlatformName(platform)} · {when}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    <Typography
                      variant="body2"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        minHeight: 60,
                      }}
                    >
                      {post.content}
                    </Typography>

                    <Box sx={{ mt: 'auto' }}>
                      <PlatformOutcomes platforms={post.platforms} results={post.results} />
                    </Box>

                    {/* Totals across the platforms that reported. Renders nothing when there is
                        no engagement to show, so an unmeasured card does not gain a row of
                        zeros — the detail view carries the per-platform breakdown. */}
                    <Box sx={{ mt: 0.75 }}>
                      <PostEngagement results={post.results} variant="compact" />
                    </Box>

                    {firstProblem && (
                      <Typography
                        variant="caption"
                        color={firstProblem.status === 'rejected' ? 'warning.main' : 'error.main'}
                        sx={{ mt: 0.5 }}
                      >
                        {(firstProblem.error_message || '').replace(/^[a-z_]+:\s*/, '')}
                      </Typography>
                    )}
                  </CardContent>
                </Box>

                {/* Actions available for this post's status. A published post shows none,
                    so the row collapses rather than showing disabled buttons — which also
                    scopes Preview to posts that have not gone out yet. A published post is
                    no longer a question of how it will look, and the detail dialog still
                    carries the preview for one. */}
                {(meta.canPublish || meta.canSchedule || meta.canEdit || meta.canDelete) && (
                  <CardActions sx={{ borderTop: 1, borderColor: 'divider', px: 1, py: 0.5 }}>
                    {/* First, and labelled rather than an icon: checking how a post reads is
                        what you do before publishing it, and the preview buried at the foot of
                        the detail dialog was missed entirely. */}
                    <Tooltip title="See how it will look on each network" arrow>
                      <Button
                        size="small"
                        startIcon={<PreviewIcon fontSize="small" />}
                        onClick={() => setPreviewing(post)}
                      >
                        Preview
                      </Button>
                    </Tooltip>
                    {meta.canPublish && (
                      <Tooltip title="Publish now" arrow>
                        <Button
                          size="small"
                          startIcon={<PublishIcon fontSize="small" />}
                          disabled={busy}
                          onClick={() => actions.publish(post)}
                        >
                          Publish
                        </Button>
                      </Tooltip>
                    )}
                    {meta.canSchedule && (
                      <Tooltip title={post.status === 'scheduled' ? 'Change the time' : 'Schedule for later'} arrow>
                        <Button
                          size="small"
                          startIcon={<ScheduleIcon fontSize="small" />}
                          disabled={busy}
                          onClick={() => setScheduling(post)}
                        >
                          {post.status === 'scheduled' ? 'Reschedule' : 'Schedule'}
                        </Button>
                      </Tooltip>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    {meta.canEdit && (
                      <Tooltip title="Edit" arrow>
                        <IconButton size="small" disabled={busy} onClick={() => setEditing(post)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {meta.canDelete && (
                      <Tooltip title="Delete" arrow>
                        <IconButton
                          size="small"
                          disabled={busy}
                          onClick={() => setConfirmDelete(post)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </CardActions>
                )}
              </Card>
            );
          })}
        </Box>
      )}

      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 3 }}>
          <Pagination
            count={pageCount}
            page={page + 1}
            onChange={(_, value) => {
              setPage(value - 1);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}

      {total > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 1 }}
        >
          Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
        </Typography>
      )}

      {/* Publishing or editing closes the preview first: both answer the question it was
          opened to ask, and Edit over the top would stack a dialog on a dialog. Only a
          scheduled post gets a date — for a draft `formatWhen` falls back to its creation
          time, which is not a promise about when it goes out. */}
      <PostPreviewDialog
        post={previewing}
        targets={targets}
        when={previewing?.status === 'scheduled' ? formatWhen(previewing) : undefined}
        busy={actions.busyId === previewing?.id}
        onPublish={
          previewing && statusMeta(previewing.status).canPublish
            ? p => { setPreviewing(null); actions.publish(p as PostRow); }
            : undefined
        }
        onEdit={
          previewing && statusMeta(previewing.status).canEdit
            ? p => { setPreviewing(null); setEditing(p as PostRow); }
            : undefined
        }
        onClose={() => setPreviewing(null)}
      />

      <PostDetailDialog
        post={selected}
        targets={targets}
        busy={actions.busyId === selected?.id}
        onClose={() => {
          setSelected(null);
          // Drop `?post=` as well, or the effect above immediately reopens it — and a
          // subsequent click on the same post from the Activity Log would be a no-op
          // because the URL never changed.
          if (searchParams.get('post')) {
            searchParams.delete('post');
            setSearchParams(searchParams, { replace: true });
            setLinkedPost(null);
          }
        }}
        onPublish={p => actions.publish(p)}
        onSchedule={p => setScheduling(p as PostRow)}
        // Closed first so a refusal lands in the page's error banner, not behind the dialog
        onStage={p => { setSelected(null); actions.stage(p); }}
        // Closed first, like onStage, so the outcome shows on the page rather than behind the dialog
        onRetry={(p, platform, republish, mediaUrls) => { setSelected(null); actions.retry(p, platform, republish, mediaUrls); }}
        onEdit={p => setEditing(p as PostRow)}
        onDelete={p => setConfirmDelete(p as PostRow)}
      />

      {editing && (
        <EditPostDialog
          open
          post={editing}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}

      {scheduling && (
        <SchedulePostDialog
          open
          platforms={scheduling.platforms}
          postId={scheduling.id}
          initial={scheduling.scheduled_for}
          initialVariants={scheduling.schedule_variants}
          rescheduling={scheduling.status === 'scheduled'}
          busy={actions.busyId === scheduling.id}
          onClose={() => setScheduling(null)}
          onConfirm={async (iso, variants) => {
            const ok = await actions.schedule(scheduling, iso, variants);
            if (ok) setScheduling(null);
          }}
        />
      )}

      <DeletePostDialog
        post={confirmDelete}
        busy={actions.busyId === confirmDelete?.id}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={async (post, removeFromPlatforms) => {
          const ok = await actions.remove(post as PostRow, removeFromPlatforms);
          if (ok) {
            setConfirmDelete(null);
            setSelected(null);
          }
          return ok;
        }}
      />

      <Snackbar
        open={Boolean(actions.notice)}
        autoHideDuration={6000}
        onClose={actions.clearMessages}
        message={actions.notice}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};
